// ─────────────────────────────────────────────────────────────────────────────
// audio_texture.ts — Continuous-noise paper texture, default tenant of
// the texture channel
//
// One persistent signal chain:
//
//   noiseLoop ─→ filter ─→ gain ─→ channel.input
//                  ▲
//                  │
//                lfo (filter freq modulation)
//
// Driven by `setScratchMotion(speed, angle, pressure)` — pen-on-paper
// noise. Volume rises past a knee, pitch tracks speed, direction adds a
// faint pitch wobble, pressure tilts level.
//
// Cast mode (`setCastMode(active)`) biases the same scratch path —
// filter sits a touch lower and Q a touch tighter — so the user hears
// the same paper tooth with a slightly muffled / resonant timbre while
// the wand is up. NO separate hum oscillator: the previous "drone over
// scratch" architecture cross-bled when the master gain opened, which
// produced an unwanted hum. One path is enough.
// ─────────────────────────────────────────────────────────────────────────────

import type { Instrument } from './audio_instrument';
import type { AudioChannel } from './audio_channel';

interface TextureDefaults {
  scratchFilterHz: number;     // base filter centre (slow speeds)
  scratchFilterQ: number;
  scratchLfoHz: number;
  scratchLfoSwingHz: number;
  scratchPeakGain: number;     // gain at full intensity
}

const DEFAULTS: TextureDefaults = {
  scratchFilterHz:    1500,        // base — modulated upward by speed
  scratchFilterQ:     0.7,
  scratchLfoHz:       4,
  scratchLfoSwingHz:  60,
  scratchPeakGain:    0.10,        // tuned good — paper tooth, not a hum
};

/** Cast mode tweaks: filter sits a touch lower, Q a touch tighter, peak
 *  gain dropped a notch so the cast scratch feels muffled-resonant
 *  vs the bright pen-on-paper baseline. */
const CAST_FILTER_HZ = 700;
const CAST_FILTER_Q  = 1.4;
const CAST_GAIN_MUL  = 0.6;

const TIME_CONSTANT_FAST = 0.03;   // 30 ms — tight gain/pitch tracking
const TIME_CONSTANT_SLOW = 0.18;   // 180 ms — cast mode entries / exits

// ── Motion → audio mapping constants ──────────────────────────────────────
// Pen-on-paper "doesn't make noise when slow". A clear knee enforces this.

/** Below this speed (px/ms) the scratch is silent. Pen idling/very slow
 *  → no audio. */
const SPEED_KNEE_PXMS = 0.25;
/** At this speed the scratch is at full intensity; faster doesn't get
 *  louder, only brighter via filter sweep. */
const SPEED_PEAK_PXMS = 1.20;

/** How much speed pushes the filter centre upward (Hz per px/ms). */
const PITCH_PER_SPEED = 700;
/** Direction-of-motion adds a small pitch wobble — "anisotropic paper". */
const PITCH_ANGLE_AMPLITUDE_HZ = 90;

export class TextureInstrument implements Instrument {
  private readonly _gain: GainNode;
  private readonly _filter: BiquadFilterNode;
  private readonly _lfo: OscillatorNode;
  private readonly _lfoGain: GainNode;
  private readonly _noise: AudioBufferSourceNode;

  private _defaults: TextureDefaults = { ...DEFAULTS };
  private _castMode = false;

  constructor(
    private readonly _ctx: AudioContext,
    private readonly _channel: AudioChannel,
  ) {
    this._gain = _ctx.createGain();
    this._gain.gain.value = 0;
    this._gain.connect(_channel.input);

    this._filter = _ctx.createBiquadFilter();
    this._filter.type = 'bandpass';
    this._filter.frequency.value = DEFAULTS.scratchFilterHz;
    this._filter.Q.value = DEFAULTS.scratchFilterQ;
    this._filter.connect(this._gain);

    this._lfo = _ctx.createOscillator();
    this._lfo.type = 'sine';
    this._lfo.frequency.value = DEFAULTS.scratchLfoHz;
    this._lfoGain = _ctx.createGain();
    this._lfoGain.gain.value = 0;
    this._lfo.connect(this._lfoGain).connect(this._filter.frequency);
    this._lfo.start();

    this._noise = _ctx.createBufferSource();
    this._noise.buffer = makeNoiseBuffer(_ctx, 2);
    this._noise.loop = true;
    this._noise.connect(this._filter);
    this._noise.start();
  }

  // ── Scratch driver ────────────────────────────────────────────────────

  /** Drive the scratch from raw motion measurements:
   *
   *    speed     px/ms   pen velocity. Below SPEED_KNEE → silent.
   *                       Volume tops out at SPEED_PEAK; pitch keeps
   *                       brightening past it.
   *    angleRad  rad     instantaneous direction of motion. Adds a
   *                       faint pitch wobble (anisotropic paper).
   *    pressure  0..1    light pressure attenuates the level.
   *                       Defaults to 0.6 when stylus doesn't report. */
  setScratchMotion(
    speed: number,
    angleRad: number = 0,
    pressure: number = 0.6,
  ): void {
    const now = this._ctx.currentTime;

    const speedNorm = (speed - SPEED_KNEE_PXMS)
      / (SPEED_PEAK_PXMS - SPEED_KNEE_PXMS);
    const intensity = Math.max(0, Math.min(1, speedNorm));
    const pressureGain = 0.5 + 0.5 * Math.max(0, Math.min(1, pressure));
    const castMul = this._castMode ? CAST_GAIN_MUL : 1;
    const targetGain = intensity * pressureGain * this._defaults.scratchPeakGain * castMul;

    this._gain.gain.setTargetAtTime(targetGain, now, TIME_CONSTANT_FAST);

    const baseFilter = this._castMode ? CAST_FILTER_HZ : this._defaults.scratchFilterHz;
    const pitch = baseFilter
      + Math.max(0, speed) * PITCH_PER_SPEED
      + Math.sin(angleRad * 2) * PITCH_ANGLE_AMPLITUDE_HZ
      + (pressure - 0.5) * 200;
    this._filter.frequency.setTargetAtTime(pitch, now, TIME_CONSTANT_FAST);

    this._lfoGain.gain.setTargetAtTime(
      intensity * this._defaults.scratchLfoSwingHz, now, TIME_CONSTANT_FAST,
    );
  }

  /** Silence the scratch (e.g. on pen-up). */
  silenceScratch(): void {
    const now = this._ctx.currentTime;
    this._gain.gain.setTargetAtTime(0, now, TIME_CONSTANT_FAST);
    this._lfoGain.gain.setTargetAtTime(0, now, TIME_CONSTANT_FAST);
  }

  // ── Cast-mode bias ────────────────────────────────────────────────────

  /** Toggle cast-mode scratch bias. Filter slides to a lower centre
   *  with a tighter Q so the same paper-tooth sound takes on a
   *  muffled-resonant timbre while the wand is up. No separate
   *  oscillator is involved — this is the same scratch path. */
  setCastMode(active: boolean): void {
    this._castMode = active;
    const now = this._ctx.currentTime;
    const targetQ = active ? CAST_FILTER_Q : this._defaults.scratchFilterQ;
    this._filter.Q.setTargetAtTime(targetQ, now, TIME_CONSTANT_SLOW);
    // Filter frequency is set per-tick by setScratchMotion using the
    // cast-aware base; nothing to do here for that.
  }

  // ── Reset ─────────────────────────────────────────────────────────────

  /** Default state: scratch at 0, cast bias off, filter back to scratch
   *  defaults. Called by callers that want a clean slate. */
  reset(): void {
    const now = this._ctx.currentTime;
    this._defaults = { ...DEFAULTS };
    this._castMode = false;
    this._gain.gain.setTargetAtTime(0, now, TIME_CONSTANT_SLOW);
    this._filter.frequency.setTargetAtTime(DEFAULTS.scratchFilterHz, now, TIME_CONSTANT_SLOW);
    this._filter.Q.setTargetAtTime(DEFAULTS.scratchFilterQ, now, TIME_CONSTANT_SLOW);
    this._lfo.frequency.setTargetAtTime(DEFAULTS.scratchLfoHz, now, TIME_CONSTANT_SLOW);
    this._lfoGain.gain.setTargetAtTime(0, now, TIME_CONSTANT_SLOW);
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function makeNoiseBuffer(ctx: AudioContext, durationS: number): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = Math.floor(sampleRate * durationS);
  const buf = ctx.createBuffer(1, length, sampleRate);
  const data = buf.getChannelData(0);
  // Smooth-ish noise: averaged white noise to reduce harshness
  let prev = 0;
  for (let i = 0; i < length; i++) {
    const w = Math.random() * 2 - 1;
    prev = prev * 0.5 + w * 0.5;
    data[i] = prev * 0.6;
  }
  return buf;
}
